import React from 'react'
import {
  PopupTooltip,
  Icon,
  utils,
  constants as tkConstants,
} from '@adc/ui-core'
import { memoize, get, isEmpty, clone, snakeCase, every } from 'lodash'
import {
  INTERESTED_PARTY_FORM_TYPE,
  ENTITY_SIGNOR_TITLE_OPTIONS,
  ENTITY_TYPE_OPTIONS,
  INDIVIDUAL_FORM_TYPE,
} from '../constants'
import { getTotalOwnershipOfGroup } from './rules'
const { required, numericality, format, email, inclusion } =
  utils.formValidators

/**
 * Renders
 **/
export const renderOwnershipLabel = className => (
  <>
    Ownership
    <PopupTooltip
      trigger={<Icon className={className} name='info circle' color='gray' />}
      data-elm-id='form_tooltip'
      content={
        <p>
          This shows the percentage of ownership for each party with a legal
          right to the use and sale of the property.
        </p>
      }
    />
  </>
)

export const signerTitleOptions = (entityType, elmId) => {
  if (!entityType) return []
  const titleOptions = clone(ENTITY_SIGNOR_TITLE_OPTIONS[entityType])
  titleOptions.unshift({ text: 'Select', value: '' })
  titleOptions.push({ text: 'Other', value: 'OTHER' })

  return titleOptions.map(option => ({
    ...option,
    'data-elm-id': option.value
      ? `${elmId}_title_option_${snakeCase(option.value)}`
      : `${elmId}_title_option_empty`,
  }))
}

/**
 * Utils
 **/
export const normalizeOwnership = value => {
  const result = removeTrailingDot(value)
  let [integer, decimal = ''] = result.split('.')
  decimal = decimal.padEnd(2, '0').slice(0, 2)
  return `${integer}.${decimal}`
}

export const removeTrailingDot = value => {
  return value && value.endsWith('.')
    ? (value = value.replace(/\.+$/, ''))
    : value
}

export const allowNumbersAndDot = (value, previousValue) => {
  return value ? value.replace(/[^\d.]/g, '') : ''
}

export const getCurrentLevel = formKey => {
  // In order to get the current level we are using a hacky way:
  // the formKey will be like this 'parent.interestedParties[0].interestedParties[0]' as you add more children under each level
  // So we can parse the formKey based on the number of dots(which is automatically added) to get the current level
  return formKey.slice().split('.').length - 1
}

const calculateBackgroundColor = formKey => {
  const level = getCurrentLevel(formKey)
  const offWhite = 'rgb(248, 249, 250)'
  const grayExtraLight = 'rgb(233, 236, 238)'
  const grayLight = 'rgb(218, 224, 227)'
  const colors = [offWhite, grayExtraLight, grayLight]
  const arrIndex = level % 3
  const currentRGB = colors[arrIndex]
  const prevLevelRGB = arrIndex - 1 < 0 ? colors[0] : colors[arrIndex - 1]

  return { currentRGB, prevLevelRGB }
}

export const getBackgroundColor = memoize(calculateBackgroundColor)

export const entityTypeOptions = elmId =>
  ENTITY_TYPE_OPTIONS.map(option => ({
    ...option,
    'data-elm-id': `${elmId}_entity_type_option_${snakeCase(option.text)}`,
  }))

export const stateOptions = elmId => {
  const states = tkConstants.optionLists.states.map(option => ({
    ...option,
    'data-elm-id': `${elmId}_state_option_${snakeCase(option.value)}`,
  }))
  states.unshift({
    key: 'default',
    value: '',
    text: 'Select',
    'data-elm-id': `${elmId}_state_option_empty`,
  })
  return states
}

/**
 * Validators
 **/
export const entityValidator = values => {
  const errors = []
  values &&
    values.forEach((value, i) => {
      const error = []
      const { subFormType, interestedParties = [] } = value
      const hasChildren = !isEmpty(interestedParties)
      const hasNoSigner = checkNoSigner(interestedParties)
      const isInterestedParty = subFormType === INTERESTED_PARTY_FORM_TYPE
      const isOwnership100Percent =
        getTotalOwnershipOfGroup(interestedParties) === Number(100).toFixed(2)
      if (isInterestedParty && (!hasChildren || hasNoSigner)) {
        error.push('Error: The Entity must have at least 1 signer.')
      }
      if (
        isInterestedParty &&
        hasChildren &&
        !isOwnership100Percent &&
        !is33PercentEven(interestedParties)
      ) {
        error.push(
          'Error: The sum of ownership of the Individuals under the Entity must equal 100%.'
        )
      }

      if (!isEmpty(error)) {
        errors[i] = error
      }
    })
  return !isEmpty(errors) ? errors : undefined
}

// Reason for having 2 kind similar validation is that
// The parent entity is not in fieldArray, so need handle it separately
export const parentEntityValidator = (_, allValues) => {
  const errors = []
  const interestedParties = get(allValues, 'parent.interestedParties')
  const hasChildren = !isEmpty(interestedParties)
  const hasNoIndividualSigner = every(interestedParties, interestedParty => {
    const isIndividual = interestedParty.subFormType === INDIVIDUAL_FORM_TYPE
    const hasNoSigner = !interestedParty.includeSignature
    return isIndividual && hasNoSigner
  })
  const parentName = get(allValues, 'parent.entityName', '<EntityName>')
  const isOwnership100Percent =
    getTotalOwnershipOfGroup(interestedParties) === Number(100).toFixed(2)
  if (!hasChildren || hasNoIndividualSigner) {
    errors.push('Error: The Parent must have at least 1 signer.')
  }

  if (
    hasChildren &&
    !isOwnership100Percent &&
    !is33PercentEven(interestedParties)
  ) {
    errors.push(
      `Error: The ${parentName} Interested Parties must have the ownership add up to 100%.`
    )
  }
  return !isEmpty(errors) ? errors : undefined
}

export const getOwnershipValidators = type => {
  const interestedPartyOwnershipValidators = [
    required(),
    numericality({
      '>=': '0.01',
      msg: 'The Entity must have at least .01% ownership.',
    }),
    numericality({
      '<=': '100',
      msg: 'The Entity must not exceed 100% ownership.',
    }),
    format({
      with: /^\d+(\.\d{0,2})?$/i,
      msg: 'format XX.XX or XXX.XX%',
    }),
  ]

  const individualOwnershipValidators = [
    required(),
    numericality({
      '<=': '100',
      msg: 'The Individual must not exceed 100% ownership.',
    }),
    format({
      with: /^\d+(\.\d{0,2})?$/i,
      msg: 'format XX.XX or XXX.XX%',
    }),
  ]
  return type === INTERESTED_PARTY_FORM_TYPE
    ? interestedPartyOwnershipValidators
    : individualOwnershipValidators
}

const getValidationErrorMsgByType = type => {
  return {
    name: `The ${type} must have a name.`,
    state: `The ${type} must have a state selected.`,
    type: `The ${type} must have a type.`,
    title: `The ${type} must have a title.`,
    email: `The ${type} must have an email`,
  }
}

const checkNoSigner = interestedParties =>
  every(interestedParties, interestedParty => !interestedParty.includeSignature)

export const is33PercentEven = interestedParties => {
  const hasLength3 = Boolean(
    interestedParties && interestedParties.length === 3
  )
  let isEachParty33Percent = true
  hasLength3 &&
    interestedParties.forEach(party => {
      const ownership =
        party.subFormType === INTERESTED_PARTY_FORM_TYPE
          ? party.interestedPartyOwnership
          : party.individualOwnership
      if (Number(ownership) !== Number(33.33)) {
        isEachParty33Percent = false
      }
    })
  return hasLength3 && isEachParty33Percent
}

export const getErrorMsg = memoize(getValidationErrorMsgByType)

export { required, email, inclusion }
